import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import PinIcon from '../icons/PinIcon';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import NotAvailable from '../atoms/NotAvailable';
import Download from '../atoms/Download';
import DropDown from '../atoms/DropDown';
import Avatar from '../atoms/Avatar';
import DragNDropDots from '../atoms/DragNDrop';
import Score, { SCORE_TYPE } from '../atoms/Score';
import LocationIcon from '../atoms/LocationIcon';
import Loader from '../atoms/Loader';
import Toast, { ToastType } from '../atoms/Toast';
import Action from '../atoms/Action';
import MultiSportList from '../molecules/MultiSportList';
import ConfirmDialog from './ConfirmDialog';
import PlayerNameWithPopover from './PlayerNameWithPopover';
import gql, { serializeGQLObject } from '../services/gql';
import exportXLSReport, { ERROR_MESSAGE_CHECK_EMAIL, XLSExportType } from '../services/export-xls-report';
import exportPDFReport, { PDFExportPage } from '../services/export-pdf-report';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import {
  COLOR_BACKGROUND_LIGHT,
  COLOR_DARK_GRAY,
  COLOR_WHITE,
  COLOR_BORDER,
  COLOR_MIDDLE_GRAY,
  COLOR_BLUE,
  COLOR_ORANGE,
  COLOR_TEXT,
  COLOR_GREEN,
  COLOR_BLACK,
} from '../styles/colors';
import MEDIA from '../styles/media';
import User from '../types/User';
import PinnedPlayer from '../types/PinnedPlayer';
import Position from '../types/Position';
import HighSchool from '../types/HighSchool';
import Checkbox from '../atoms/Checkbox';
import Player from '../types/Player';
import { getGradClassYearsList } from '../services/user';
import { SortableTableHeaderCell } from '../atoms/SortableTableHeaderCell';
import CompareIcon from '../icons/CompareIcon';
import SaveIcon from '../icons/SaveIcon';
import AlertIcon from '../icons/AlertIcon';
import SideBySideIcon from '../icons/SideBySideIcon';
import { Typography } from '@material-ui/core';
import MultiSelect from '../molecules/MultiSelect';
import ColorCode from '../atoms/ColorCode';
import { Color } from '../types/Color';
import PlusIcon from '../icons/PlusIcon';
import Button from '../atoms/Button';
import Dialog from '../molecules/Dialog';
import Input from '../atoms/Input';
import Board from '../types/Board';
import RefreshIcon from '@material-ui/icons/RefreshRounded';

interface MyRecruitingBoardProps {
  className?: string;
  user: User;
  position?: Position;
  printed?: boolean;
  rolloverDay?: number;
  rolloverMonth?: number;
  currentRecruitingYear?: number;
}

export const TEAM_TYPE = {
  HIGH_SCHOOL: 'High School',
  COLLEGE: 'College',
  NFL: 'NFL',
};

export const FILTER_TYPE = {
  POSITION: 'position',
  CLASS: 'class',
  STATE: 'state',
  TEAM_TYPE: 'teamType'
}

const useStyles = makeStyles<Theme, MyRecruitingBoardProps>(theme => ({
  myRecruitingBoard: {
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    position: 'relative',
  },

  header: {
    display: 'flex',
    minHeight: '70px',
    padding: theme.spacing(2),
    alignItems: 'center',
  },
  headerTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(2),
  },
  headerIcon: {
    flexShrink: 0,
    width: '22px',
    height: '22px',
  },
  headerTitle: {
    ...theme.typography.h2,
    margin: theme.spacing(0, 0, 0, 1),
  },
  headerDownloads: {
    display: 'flex',
    marginLeft: 'auto',
  },
  closeButton: {
    marginLeft: 'auto',
  },
  dropDown: {
    display: 'inline-block',
    maxWidth: 120,
    minWidth: 120,
  },
  dropDownSelect: {
    width: '100%',
    height: '40px',
  },

  tableWrap: {
    maxHeight: '3200px',
    overflow: 'auto',
  },

  noPlayers: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily: FONT_PROXIMA_NOVA,
  },
  noPlayersIcon: {
    width: '50px',
    height: '50px',
    opacity: 0.5,
  },
  noPlayersText: {
    marginTop: theme.spacing(2),
    color: COLOR_DARK_GRAY,
  },

  table: {
    position: 'relative',
  },

  tableHeadCell: {
    textAlign: 'center',
    padding: theme.spacing(1.5, 0, 1.5, 1.5),
    fontSize: theme.typography.pxToRem(14),
    color: COLOR_DARK_GRAY,
    borderBottom: `1px solid ${COLOR_BORDER}`,
    lineHeight: '1rem',

    '&:first-of-type': {
      paddingLeft: theme.spacing(1.5),
    },
    '&:last-of-type': {
      paddingRight: theme.spacing(1),
    },
  },
  tableRow: {
    backgroundColor: COLOR_WHITE,
    transition: 'background 0.3s',

    '&:hover': {
      backgroundColor: COLOR_BACKGROUND_LIGHT,

      '& $dragNDropDots': {
        opacity: 1,
      },
    },
  },
  tableRowDragging: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tableCell: {
    padding: theme.spacing(1, 0, 1, 1.5),
    fontSize: theme.typography.pxToRem(16),
    border: 0,

    '&:first-of-type': {
      paddingLeft: (props) => props.printed ? theme.spacing(1) : 0,
    },
    '&:nth-of-type(2)': {
      paddingLeft: (props) => props.printed ? theme.spacing(1) : 0,
    },
    '&:last-of-type': {
      paddingLeft: (props) => props.printed ? theme.spacing(1) : theme.spacing(1),
    },
  },

  multiSportCell: {
    maxWidth: '116px',
    minWidth: '92px',
    overflow: 'hidden',
  },
  sportIcon: {
    flexShrink: 0,
  },

  tableSectionTitle: {
    background: COLOR_BACKGROUND_LIGHT,
    color: COLOR_DARK_GRAY,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1,
    padding: theme.spacing(0.5, 2),
    borderTop: '1px solid rgba(0, 0, 0, 0.04)',
    borderBottom: 0,
    position: 'relative',

    '&:after': {
      content: '""',
      position: 'absolute',
      width: '100%',
      height: '1px',
      background: 'rgba(0, 0, 0, 0.04)',
      bottom: 0,
      left: 0,
    },
  },

  playerInfoContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  playerAvatar: {
    flexShrink: 0,
    width: '32px',
    height: '32px',
  },
  playerNameAndHometown: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: theme.spacing(1),
  },
  playerHometown: {
    fontSize: theme.typography.pxToRem(12),
    color: COLOR_DARK_GRAY,
  },

  locationIcon: {
    marginLeft: 'auto',
  },

  dragNDropDots: {
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  blankCell: {
    display: 'none',
  },

  '@media (max-width: 1600px)': {
    dropDownSelect: {
      fontSize: theme.typography.pxToRem(14),
    },
  },

  '@media (min-width: 1025px) and (max-width: 1260px), (max-width: 480px)': {
    header: {
      flexWrap: 'wrap',
    },
    headerTitleWrap: {
      width: '100%',
      marginBottom: theme.spacing(1),
    },
    headerDownloads: {
      marginLeft: 0,
    },
  },

  [MEDIA.DESKTOP_LARGE]: {
    tableHeadCell: {
      padding: theme.spacing(1, 0, 1, 1.5),
      fontSize: theme.typography.pxToRem(14),

      '&:first-of-type': {
        paddingLeft: theme.spacing(1.5),
      },
      '&:last-of-type': {
        paddingRight: theme.spacing(1.5),
      },
    },
  },

  [MEDIA.MOBILE]: {
    header: {
      display: 'none',
    },
  },
  multiSportIcon: {
    color: COLOR_WHITE
  },
  checkBoxCell: {
    textAlign: 'center',
  },
  orderIcon: {
    position: 'relative',
    right: '8px',
    width: '32px',
    height: '32px',
    color: COLOR_MIDDLE_GRAY,
  },
  orderIconActive: {
    color: COLOR_BLUE,
  },
  actions: {
    display: 'flex',
    padding: theme.spacing(2.5),
    borderTop: `1px solid ${COLOR_BORDER}`,
  },
  actionsPrimary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    maxWidth: '560px',
    paddingRight: theme.spacing(4.25),
  },
  action: {
    padding: theme.spacing(0,0,0,2),
    color: COLOR_DARK_GRAY,
    outlineColor: COLOR_ORANGE,
    transition: 'color 0.3s',

    '&:first-child': {
      paddingLeft: 0
    },

    '&:hover': {
      color: COLOR_BLUE,
      textDecoration: 'underline',

      '& $actionText': {
        color: COLOR_BLUE,
      },
    },
  },
  disabledAction: {
    outlineColor: 'transparent',

    '& $actionText': {
      color: COLOR_DARK_GRAY,
    },

    '&:hover': {
      color: COLOR_DARK_GRAY,
      textDecoration: 'none',

      '& $actionText': {
        color: COLOR_DARK_GRAY,
      },
    },
  },
  actionIcon: {
    width: '20px',
    height: '20px',
  },
  actionText: {
    // display: 'none',
    fontSize: theme.typography.pxToRem(16),
    color: COLOR_TEXT,
    transition: 'color 0.3s',
  },
  speedAndPowerCell: {
    textAlign: 'center',
  },
  speedHeadCelll: {
    padding: theme.spacing(1, 0.5),
  },
  hsComTableCell: {
    padding: theme.spacing(1, 1, 1, 3),
  },
  educationDropDown: {
    flexShrink: 0,
  },
  educationDropDownSelectorRoot: {
    paddingRight: theme.spacing(5),
  },
  [MEDIA.MOBILE]: {
    educationDropDown: {
      margin: theme.spacing(1,3),
    },
  },
  noDataTextWrapper: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    height: "100%",
    minHeight: 200,
    width: "100%"
  },
  noDataText: {
    color: COLOR_DARK_GRAY,
    fontSize: theme.typography.pxToRem(14),
    margin: '0 auto',
  },
  multiSportsWrap: {
    marginLeft: theme.spacing(2)
  },
  multiSelect: {
    maxWidth: '130px',
    marginLeft: '6px',
    width: '100%',
  },
  multiSelectSelector: {
    height: '42px',
  },
  multiSelectSelectorRoot: {
    height: '28px',
    lineHeight: '28px',
    fontSize: theme.typography.pxToRem(16),
  },
  lowSpeedAndPower: {
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1,
    marginRight: theme.spacing(0.25),
  },
  hsSpeedCell: {
    color: COLOR_ORANGE,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontWeight: 600,
    textAlign: 'center',
  },
  hsPowerCell: {
    color: COLOR_GREEN,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontWeight: 600,
    textAlign: 'center',
  },
  filterWrap: {
    display: 'flex',
    padding: theme.spacing(1.25, 2.5),

    '& > $multiSelect:first-of-type': {
      maxWidth: 130,
    },

    '& $multiSelect': {
      marginLeft: theme.spacing(0), 
      paddingRight: theme.spacing(2),
    },
  },
  addBoardButton: {
    display: 'flex',
    float: 'right',
    width: 'auto',
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.75),

    '& span': {
      fontSize: theme.typography.pxToRem(14),
    }
  },
  addIcon: {
    flexShrink: 0,
    width: '12px',
    height: '12px',
    marginRight: theme.spacing(1),
  },
  boardDialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  boardDialogInput: {
    width: '100%',
  },
  boardFilterWrap: {
    display: 'flex',
    marginLeft: 'auto',
    alignItems: 'center',
  },
  emptyItemClassName: {
    color: COLOR_BLUE,
  },
  selectorRootClassName: {
    marginRight: theme.spacing(3.75),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  colorPrimary: {
    color: COLOR_BLUE,
  }
}), { name: MyRecruitingBoard.name });

export default function MyRecruitingBoard (props: MyRecruitingBoardProps) {
  const {
    className,
    user,
    position,
    printed = false,
    rolloverDay,
    rolloverMonth,
    currentRecruitingYear,
  } = props;
  const classes = useStyles(props);
  const userId = user?.id;
  const programId = user?.program?.id;
  const isCollege = user?.accessLevel?.type === 'college';

  const [pinnedPlayers, setPinnedPlayers] = useState<PinnedPlayer[]>([]);
  const [positions, setPositions] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [players, setPlayers] = useState<PinnedPlayer[]>([]);
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [graduatingClasses, setGraduatingClasses] = useState<any>([]);
  const [selectedGradClasses, setSelectedGradClasses] = useState<string[]>([]);
  const [playersIdsMapping, setPlayersIdsMapping] = useState<any>();

  const [loading, setLoading] = useState<boolean>(true);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [toastVisible, setToastVisible] = useState<boolean>(false);
  const [toastType, setToastType] = useState<ToastType>(ToastType.SUCCESS);
  const [toastMessage, setToastMessage] = useState<any>('');
  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([]);
  const [sortConfig, setSortConfig] = React.useState<{ 
    metric: 'pai' | 'combine' | 'class' | 'speed' | 'power' | 'hsSports' | 'name' | null;
    order: 'asc' | 'desc' | undefined;
  }>({ metric: null, order: undefined });
  const [selectedTeamType, setSelectedTeamType] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<string[]>([]);
  const teamTypeItems = [
    { content: 'High School', value: TEAM_TYPE.HIGH_SCHOOL },
    { content: 'College', value: TEAM_TYPE.COLLEGE },
    { content: 'NFL', value: TEAM_TYPE.NFL }
  ];
  const [newBoardName, setNewBoardName] = useState<string>('');
  const [boardDialogOpen, setBoardDialogOpen] = useState<boolean>(false);
  const [deleteBoardDialogOpen, setDeleteBoardDialogOpen] = useState<boolean>(false);
  const [boardsList, setBoardsList] = useState<Board[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<string>('');
  const [selectedBoardDetails, setSelectedBoardDetails] = useState<Board>();
  const [refreshBoards, setRefreshBoards] = useState<boolean>(false);

  useEffect(() => {
    getBoards();
    fetchPinnedPlayers();
  }, []);

  // refresh
  useEffect(() => {
    if (refreshBoards) {
      getBoards();
      fetchPinnedPlayers();
      setRefreshBoards(false);
    }
  }, [refreshBoards]);

  useEffect(() => {
    if (boardsList.length) {
      // let order = boardsList.find(board => board.id === Number(selectedBoard))?.order || [];
      if (selectedBoardDetails) {
        let order = selectedBoardDetails?.order || [];
        updateAndSetPlayersData(orderPlayers(pinnedPlayers, order));
      }
    }
  }, [boardsList, selectedBoardDetails]);

  useEffect(() => {
    if (!players.length) return;
  
    let filtered = [...players];
  
    if (selectedGradClasses?.length) {
      const gradClassesAsStrings: string[] = selectedGradClasses.map(String);
      filtered = filtered.filter(
        (p: PinnedPlayer) =>
        gradClassesAsStrings.includes(String(p.player?.highSchools?.[0]?.graduatingClass))
      );
    }
  
    if (!!selectedPositions?.length) {
      filtered = filtered.filter(
        (p: PinnedPlayer) => selectedPositions.includes(p.position)
      );
    }
    // Filter by team type
    const filterBeforeType = filtered;
    // I want to filter by team type and then add the filtered players to the filtered array and this is multiselect so multiple filters can be applied
    const filteredByType = [];
    if (selectedTeamType.includes('High School')) {
      let filtered = filterBeforeType.filter(
        (p) => p.player?.hasHighSchool === true && p.player?.hasCollegeTeam === false && p.player?.hasNFLTeam === false
      );
      filteredByType.push(...filtered);
    }
    if (selectedTeamType.includes('College')) {
      let filtered = filterBeforeType.filter(
        (p) => p.player?.hasHighSchool === true && p.player?.hasCollegeTeam === true && p.player?.hasNFLTeam === false
      );
      filteredByType.push(...filtered);
    } 
    if (selectedTeamType.includes('NFL')) {
      let filtered = filterBeforeType.filter(
        (p) => p.player?.hasHighSchool === true && p.player?.hasCollegeTeam === true && p.player?.hasNFLTeam === true
      );
      filteredByType.push(...filtered);
    }
    if (selectedTeamType.length) {
      filtered = filteredByType;
    }

    // state filter
    if (selectedState?.length) {
      filtered = filtered.filter((pinnedPlayer) => {
        const highSchools: HighSchool[] = pinnedPlayer.player.highSchools || [];
        const primary = highSchools.find((h) => h?.isPrimary);
        return primary?.state && selectedState.includes(primary.state);
      });
    }

    // sorting
    if (sortConfig?.metric && sortConfig?.order) {
      const descending = sortConfig.order === 'desc';
      filtered = sortPlayersByPrimaryMetric(filtered, sortConfig.metric, descending);
    } else {
      if (selectedBoardDetails) {
        // const order = boardsList.find(board => board.id === Number(selectedBoard))?.order || [];
        const order = selectedBoardDetails?.order || [];
        filtered = orderPlayers(filtered.sort((a, b) => a.order - b.order), order);
      } else {
        filtered = orderPlayersByOrder(filtered);
      }
    }
  
    setPinnedPlayers(filtered);
  }, [
    selectedGradClasses, 
    selectedPositions, 
    players, 
    sortConfig, 
    selectedTeamType, 
    selectedState,
  ]);

  React.useEffect(() => {
    if (!!sortConfig?.order) {
      if (!sortConfig.metric || !sortConfig.order) {
        setPlayers(players); // no sort
        setPinnedPlayers(pinnedPlayers); // no sort
        return;
      }
      
      const descending = sortConfig.order === 'desc';
    
      const sorted = sortPlayersByPrimaryMetric(pinnedPlayers, sortConfig.metric, descending);
      // setPlayers(sorted);
      setPinnedPlayers(sorted);
    }
  }, [sortConfig]);  

  function sortPlayersByPrimaryMetric(
    players: any[],
    metricKey: 'pai' | 'combine' | 'class' | 'speed' | 'power' | 'hsSports' | 'name',
    descending: boolean
  ) {
    return [...players].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;
  
      const IS_CLASS_METRIC = metricKey === 'class';
      const IS_HS_SPEED_METRIC = metricKey === 'speed';
      const IS_HS_POWER_METRIC = metricKey === 'power';
      const IS_HS_SPORTS_METRIC = metricKey === 'hsSports';
      const IS_NAME_METRIC = metricKey === 'name';
  
      if (IS_CLASS_METRIC) {
        aVal = a.player?.highSchools?.find((hs: any) => hs?.isPrimary)?.graduatingClass ?? 0;
        bVal = b.player?.highSchools?.find((hs: any) => hs?.isPrimary)?.graduatingClass ?? 0;
      } else if (IS_HS_SPEED_METRIC || IS_HS_POWER_METRIC) {
        aVal = IS_HS_SPEED_METRIC ? (a.player?.hsSpeedPercentile ?? 0) : (a.player?.hsPowerPercentile ?? 0);
        bVal = IS_HS_SPEED_METRIC ? (b.player?.hsSpeedPercentile ?? 0) : (b.player?.hsPowerPercentile ?? 0);
      } else if (IS_HS_SPORTS_METRIC) {
        aVal = a.player?.multiSport?.length ?? 0;
        bVal = b.player?.multiSport?.length ?? 0;
      } else if (IS_NAME_METRIC) {
        aVal = a.player?.firstName ?? '';
        bVal = b.player?.firstName ?? '';
        return descending
          ? (bVal as string).localeCompare(aVal as string)
          : (aVal as string).localeCompare(bVal as string);
      } else {
        aVal = a.player[metricKey]?.find((m: any) => m?.isPrimary)?.[metricKey] ?? 0;
        bVal = b.player[metricKey]?.find((m: any) => m?.isPrimary)?.[metricKey] ?? 0;
      }
  
      return descending ? (bVal as number) - (aVal as number) : (aVal as number) - (bVal as number);
    });
  }  

  function fetchPinnedPlayers () {
    setLoading(true);

    gql(`
      pinnedPlayers {
        id
        playerId
        player {
          id
          firstName
          lastName
          slug
          photoUrl
          pai
          combine
          multiSport
          hasCollegeTeam
          hasNFLTeam
          hasHighSchool
          slug
          hsCombines {
            id
          }
          highSchools
          hsSpeedPercentile
          hsPowerPercentile
          isSpeedRecruit
          isPowerRecruit
        }
        order
        position
        createdAt
      }
    `)
    .then((data:any) => data.pinnedPlayers as any)
    .then((players:PinnedPlayer[]) => {
      if (players.length) {
        // updateAndSetPlayersData(players);
        if (selectedBoardDetails) {
          let order = selectedBoardDetails?.order || [];
          updateAndSetPlayersData(orderPlayers(players, order.map(Number)), true);
        } else {
          updateAndSetPlayersData(orderPlayersByOrder(players), true);
        }
      }
    })
    .catch(console.error)
    .finally(() => setLoading(false));
  }

  function orderPlayersByOrder(players:PinnedPlayer[]) {
  
    // 1. First reorder based on playerIds
    const ordered = players.filter(player => player.order !== null).sort((a, b) => a.order - b.order);
  
    // 2. Then append the rest (not included in playerIds)
    const remaining = players.filter(player => player.order == null).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  
    return [...ordered, ...remaining];
  }

  function orderPlayers(players:PinnedPlayer[], playerIds:number[]) {
    const idSet = new Set(playerIds);
  
    // 1. First reorder based on playerIds
    const ordered = playerIds.map(id => players.find(player => player?.id === id)).filter((player): player is PinnedPlayer => player !== undefined);
  
    // 2. Then append the rest (not included in playerIds)
    const remaining = players.filter(player => !idSet.has(player?.id));
  
    return [...ordered, ...remaining];
  }

  function updateAndSetPlayersData (pinnedPlayers:PinnedPlayer[], setPlayersState: boolean = false) {
    setPinnedPlayers(pinnedPlayers);
    if (setPlayersState) {
      setPlayers(pinnedPlayers);
      updateStates(pinnedPlayers);
      updateClasses(pinnedPlayers);
      updatePositions(pinnedPlayers);
      groupPlayersByPosition(pinnedPlayers);
    }
  }

  function unpinPlayers(playerIds:(number | string | undefined) []) {
    return gql(`
      mutation {
        unpin (playerIds: [${playerIds.map(playerId => playerId)}])
      }
    `)
      .then((data:any) => data && data.unpin as boolean)
      .catch(() => {
        showToast('Oops, something is wrong. Try again or contact our Support team.', ToastType.ERROR);
      });
  }

  function unpinSinglePlayer() {
    setLoading(true);
    const playerIds = selectedPlayers.map((selectedItem:Player) => selectedItem.id);
    unpinPlayers(playerIds)
      .then((success:boolean | void) => {
        if (success) {
          showToast('Unpinned', ToastType.SUCCESS);
          const pinndePlayersAfterUnpin = pinnedPlayers.filter((player: PinnedPlayer) => player?.playerId && !playerIds.includes(player?.playerId));

          updateAndSetPlayersData(pinndePlayersAfterUnpin);
        } else {
          showToast('Oops, something is wrong. Try again or contact our Support team.', ToastType.ERROR);
        }
      })
      .finally(() => setLoading(false));
  }

  function handleOpenClearDialog() {
    setDialogOpen(true)
  };

  function clearBoard() {
    setLoading(true);
    setDialogOpen(false);

    unpinPlayers(pinnedPlayers.map(pinnedPlayer => pinnedPlayer.playerId))
      .then((success:boolean | void) => {
        if (success) {
          showToast('All players unpinned', ToastType.SUCCESS);
          updateAndSetPlayersData([]);
        } else {
          showToast('Oops, something is wrong. Try again or contact our Support team.', ToastType.ERROR);
        }
      })
      .finally(() => {
        setLoading(false);
        setSelectedPositions([position ?? '']);
      });
  }

  function handleSortClick(metric: 'pai' | 'combine' | 'class' | 'speed' | 'power' | 'hsSports' | 'name') {
    setSortConfig(prev => {
      // switching to a new metric, reset order
      if (prev.metric !== metric) {
        return { metric, order: 'asc' };
      }
  
      if (prev.order === 'asc') return { metric, order: 'desc' };
      return { metric, order: 'asc' };
    });
  }  

  function reorderPinnedPlayers (playerIds:number[], reorderedPlayers:PinnedPlayer[]) {
    setLoading(true);
    if (selectedBoardDetails) {
      gql(`
        mutation {
          updateBoard (
          Board: {
            id: ${Number(selectedBoardDetails?.id)},
            order: [${playerIds}],
          }
          )
        }
      `)
      .then((data: any) => data && data.updateBoard as boolean)
      .then((success: boolean) => {
        if (success) {
          // getBoards();
          getboardById(selectedBoardDetails?.id.toString());
          fetchPinnedPlayers();
          // showToast('Success.', ToastType.SUCCESS);
          setPinnedPlayers(reorderedPlayers);
        }
      })
      .catch(() => showToast('Failed to save the new order. Please refresh the page.', ToastType.ERROR))
      .finally(() => setLoading(false));
    } else {
      gql(`
        mutation {
          reorderPinnedPlayers (
            pinnedPlayersIds: [${playerIds}],
            position: "${selectedPositions}"
          )
        }
      `)
        .then((data:any) => data && data.reorderPinnedPlayers as boolean)
        .then((success:boolean) => {
          if (success) {
            fetchPinnedPlayers();
            showToast('Success.', ToastType.SUCCESS);
            setPinnedPlayers(reorderedPlayers);
          } else {
            showToast('Failed to save the new order. Please refresh the page.', ToastType.ERROR);
          }
        })
        .catch(() => showToast('Failed to save the new order. Please refresh the page.', ToastType.ERROR))
        .finally(() => setLoading(false));
    }
  }

  function groupPlayersByPosition(players:PinnedPlayer[]) {
    const groupedPlayers = [...players]
      .sort((a: PinnedPlayer, b: PinnedPlayer) => {
        if (a.position < b.position) { return -1; }
        if (a.position > b.position) { return 1; }
        return a.order - b.order;
      });

    setPinnedPlayers(groupedPlayers);

    let idsMapping:{[key: number]: number} = {};
    groupedPlayers.map((pinnedPlayer: any) => {
      idsMapping[pinnedPlayer.playerId] = pinnedPlayer.id;
    });
    setPlayersIdsMapping(idsMapping);

    setPlayers(groupedPlayers);
  }

  function updatePositions(pinnedPlayers:PinnedPlayer[]) {
    const positions = pinnedPlayers
      .map(pinnedPlayer => pinnedPlayer.position)
      .sort();

      setPositions(positions
        .filter((position:Position, index:number) => positions.indexOf(position) === index)
        .map((position:Position) => ({
          content: position,
          value: position,
        }))
      );
  }

  function updateClasses(players:PinnedPlayer[]) {
    const accessLevel = user.accessLevel;
    let graduatingClasses: any[] = [];
    if (rolloverDay !== undefined && rolloverMonth !== undefined && currentRecruitingYear !== undefined) {
      const gradYears = getGradClassYearsList(
        rolloverDay,
        rolloverMonth,
        accessLevel?.hsYearsAugust1 || [],
        accessLevel?.hsYearsMarch1 || [],
        currentRecruitingYear,
        false
      );
      
      graduatingClasses = Array.from(
        new Set(gradYears)
        ).sort((a, b) => b - a)
        .map(year => ({ content: year, value: year }));
        
    }

    setGraduatingClasses(graduatingClasses);
  }

  function updateStates(pinnedPlayers:PinnedPlayer[]) {
    const primaryHighSchoolStates: string[] = pinnedPlayers
      .map((pinnedPlayer) => {
        const highSchools: HighSchool[] = pinnedPlayer.player.highSchools || [];
        const primary = highSchools.find((h) => h?.isPrimary);
        return primary?.state;
      })
      .filter((state): state is string => Boolean(state))
      .sort();  
    
    const statesList = primaryHighSchoolStates
      .filter((state: string, index: number) => primaryHighSchoolStates.indexOf(state) === index)
      .map((state) => ({
        content: state,
        value: state,
      })
    );

    setStates(statesList); 
  }

  function onDragEnd (result:any) {
    if (!result.destination) return;

    const fromPosition = result.source.index;
    const toPosition = result.destination.index;
    const reorderedPlayers = [...pinnedPlayers];
    const draggedPlayer = pinnedPlayers[fromPosition];

    reorderedPlayers.splice(fromPosition, 1); // cut out the dragged player
    reorderedPlayers.splice(toPosition, 0, draggedPlayer); // insert it to the position

    const playerIds = reorderedPlayers.map((pinnedPlayer:PinnedPlayer) => playersIdsMapping[pinnedPlayer.player.id]);

    reorderPinnedPlayers(playerIds, reorderedPlayers);
  }

  function showToast (message:any, type:ToastType = ToastType.SUCCESS) {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  }

  function onXLSDownload () {
    setLoading(true);
    const playerIds = selectedPlayers?.length 
      ? selectedPlayers?.map((player:Player) => player.id)
      : players.map((pinnedPlayer:PinnedPlayer) => pinnedPlayer.playerId);

    exportXLSReport(
      XLSExportType.PLAYERS,
      { ids: playerIds, board: true }
    )
      .catch(error => {
        if (error.message === ERROR_MESSAGE_CHECK_EMAIL) {
          showToast('Check e-mail. You should receive the download link in several minutes', ToastType.SUCCESS);
        } else {
          console.error(error);
          showToast(<>Failed to download XLS report. <br />({error.message})</>, ToastType.ERROR);
        }
      })
      .finally(() => setLoading(false));
  }

  function onPDFDownload () {
    setLoading(true);

    exportPDFReport(
      PDFExportPage.RECRUITING_BOARD,
      '',
      selectedPositions?.length ? { position: selectedPositions } : undefined,
    )
      .catch(error => {
        console.error(error);
        showToast(<>Failed to download PDF report. <br />({error.message})</>, ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function onAlert () {
    setLoading(true);

    gql(`
      mutation {
        addAlerts (playerIds: [${selectedPlayers.map((selectedItem:Player) => selectedItem.id)}])
      }
    `)
      .then((data:any) => ({
        success: data.addAlerts as boolean,
        errorMessage: data.errorMessage,
      }))
      .then(({ success, errorMessage }:{ success:boolean, errorMessage:string }) => {
        if (success) {
          showToast('Alert added', ToastType.SUCCESS);
        } else if (errorMessage) {
          showToast(errorMessage, ToastType.ERROR);
        } else {
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));

    setSelectedPlayers([]);
  }

  function onSave () {
    setLoading(true);

    gql(`
      mutation {
        savePlayers (playerIds: [${selectedPlayers.map((selectedItem:Player) => selectedItem.id)}], folderId: null)
      }
    `)
      .then((data:any) => ({
        success: data.savePlayers as boolean,
        errorMessage: data.errorMessage,
      }))
      .then(({ success, errorMessage }:{ success:boolean, errorMessage:string }) => {
        if (success) {
          showToast('Saved', ToastType.SUCCESS);
        } else if (errorMessage) {
          showToast(errorMessage, ToastType.ERROR);
        } else {
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));

    setSelectedPlayers([]);
  }

  function onCheckBoxChange (player:Player) {
    return (value: boolean) => {
      if (value) {
        setSelectedPlayers([...selectedPlayers, player]);
      } else {
        const selectedItemsWithoutCheckedItem = [...selectedPlayers];
        const removeIndex = selectedPlayers.findIndex((item) => item.id === player.id);
        if (removeIndex > -1) {
          selectedItemsWithoutCheckedItem.splice(removeIndex, 1);
        }
        setSelectedPlayers(selectedItemsWithoutCheckedItem);
      }
    };
  }

  function handlePositionChange(newValues: string[]) {
    const allValue = 'all';
    const allOptionValues = positions.map((p: { value: any; }) => p.value);
  
    const isAllChecked = newValues.includes(allValue);
    const isAllOptionSelected = selectedPositions.includes(allValue);
    const isCurrentlyAllSelected =
      selectedPositions.length === allOptionValues.length + 1 && isAllOptionSelected;
    let filteredPosition: string[] = []

    // || (!isAllChecked && newValues.length < selectedPositions?.length)
    if ((isAllChecked && !isCurrentlyAllSelected)) {
      // "All" was clicked and not everything is selected → select all
      filteredPosition = [allValue, ...allOptionValues];
    } else if (!isAllChecked && isAllOptionSelected) {
      // "All" was unchecked → reset selection
      filteredPosition = [];
    } else if (isAllChecked && newValues.length < selectedPositions?.length) {
      const filtered = newValues.filter(val => val !== allValue);
      filteredPosition = filtered;
    } else if (!isAllChecked && !isAllOptionSelected && newValues?.length === positions?.length) {
      // "All" was checked and all positions selected, uncheck "all" 
      filteredPosition = [allValue, ...allOptionValues];
    } else {
      // Normal multi-select behavior
      filteredPosition = newValues;
    }
    setSelectedPositions(filteredPosition);
    // update board on filter change when there is a selected board
    updateBoard(filteredPosition, FILTER_TYPE.POSITION);
  };

  function handleClassChange(newValues: string[]) {
    const allValue = 'all';
    const allOptionValues = graduatingClasses.map((p: { value: any; }) => p.value);
  
    const isAllChecked = newValues.includes(allValue);
    const isAllOptionSelected = selectedGradClasses.includes(allValue);
    const isCurrentlyAllSelected =
    selectedGradClasses.length === allOptionValues.length + 1 && isAllOptionSelected;
    let filteredClass: string[] = []

    if ((isAllChecked && !isCurrentlyAllSelected)) {
      // "All" was clicked and not everything is selected → select all
      filteredClass = [allValue, ...allOptionValues];
    } else if (!isAllChecked && isAllOptionSelected) {
      // "All" was unchecked → reset selection
      filteredClass = [];
    } else if (isAllChecked && newValues.length < selectedGradClasses?.length) {
      const filtered = newValues.filter(val => val !== allValue);
      filteredClass = filtered;
    } else if (!isAllChecked && !isAllOptionSelected && newValues?.length === graduatingClasses?.length) {
      // "All" was checked and all classes selected, uncheck "all" 
      filteredClass = [allValue, ...allOptionValues];
    } else {
      // Normal multi-select behavior
      filteredClass = newValues;
    }
    setSelectedGradClasses(filteredClass);
    // update board on filter change when there is a selected board
    updateBoard(filteredClass, FILTER_TYPE.POSITION);
  };

  function handleTeamTypeChange(newValues: string[]) {
    const allValue = 'all';
    const allOptionValues = teamTypeItems.map((p: { value: any; }) => p.value);
  
    const isAllChecked = newValues.includes(allValue);
    const isAllOptionSelected = selectedTeamType.includes(allValue);
    const isCurrentlyAllSelected =
    selectedTeamType.length === allOptionValues.length + 1 && isAllOptionSelected;
    let filterTeamType: string[] = [];

    if ((isAllChecked && !isCurrentlyAllSelected)) {
      // "All" was clicked and not everything is selected → select all
      filterTeamType = [allValue, ...allOptionValues];
    } else if (!isAllChecked && isAllOptionSelected) {
      // "All" was unchecked → reset selection
      filterTeamType = [];
    } else if (isAllChecked && newValues.length < selectedTeamType?.length) {
      const filtered = newValues.filter(val => val !== allValue);
      filterTeamType = filtered;
    } else if (!isAllChecked && !isAllOptionSelected && newValues?.length === teamTypeItems?.length) {
      // "All" was checked and all classes selected, uncheck "all" 
      filterTeamType = [allValue, ...allOptionValues];
    }else {
      // Normal multi-select behavior
      filterTeamType = newValues;
    }
    setSelectedTeamType(filterTeamType);
    // update board on filter change when there is a selected board
    updateBoard(filterTeamType, FILTER_TYPE.TEAM_TYPE);
  }

  function handleStateChange(newValues: string[]) {
    const allValue = 'all';
    const allOptionValues = states.map((p: { value: any; }) => p.value);
  
    const isAllChecked = newValues.includes(allValue);
    const isAllOptionSelected = selectedState.includes(allValue);
    const isCurrentlyAllSelected =
      selectedState.length === allOptionValues.length + 1 && isAllOptionSelected;
    let filteredStates: string[] = []
    if ((isAllChecked && !isCurrentlyAllSelected)) {
      // "All" was clicked and not everything is selected → select all
      filteredStates = [allValue, ...allOptionValues];
    } else if (!isAllChecked && isAllOptionSelected) {
      // "All" was unchecked → reset selection
      filteredStates = [];
    } else if (isAllChecked && newValues.length < selectedState?.length) {
      const filtered = newValues.filter(val => val !== allValue);
      filteredStates = filtered;
    } else if (!isAllChecked && !isAllOptionSelected && newValues?.length === states?.length) {
      // "All" was checked and all states selected, uncheck "all" 
      filteredStates = [allValue, ...allOptionValues];
    } else {
      // Normal multi-select behavior
      filteredStates = newValues;
    }
    setSelectedState(filteredStates);
    // update board on filter change when there is a selected board
    updateBoard(filteredStates, FILTER_TYPE.STATE);
  }

  function getBoards(newBoardName: string = '') {
    setLoading(true);

    gql(`
      getBoards(${isCollege ? `programId: ${programId}` : `userId: ${userId}`}) {
        id
        name
        userId
        programId
        position
        class
        type
        state
        collegeType
        order
        createdAt
        updatedAt
      }
    `)
    .then((data:any) => data.getBoards as any)
    .then((board: Board[]) => {
      if (board?.length) {
        setBoardsList(board);
        if (newBoardName) {
          const newBoard = board?.find((b) => b.name === newBoardName)
          const boardId = String(newBoard?.id)
          boardId && getboardById(boardId);
        }
      };
    })
    .catch(console.error)
    .finally(() => setLoading(false));
  }

  // Add a new Board
  function addBoardHandler() {
    setLoading(true);
    // remove 'all' option from the setters
    const positions = filterAllFromSelectedItem(selectedPositions);
    const gradClasses = filterAllFromSelectedItem(selectedGradClasses);
    const teamTypes = filterAllFromSelectedItem(selectedTeamType);
    const states = filterAllFromSelectedItem(selectedState);
    const boardPayload = {
      name: newBoardName,
      position: positions,
      class: gradClasses,
      type: teamTypes,
      state: states,
      userId: userId,
      order: [...pinnedPlayers.map(player => player.id)],
    }
    gql(`
      mutation {
        saveBoard(
          Board: ${serializeGQLObject(boardPayload)}
        )
      }
    `)
      .then((data:any) => ({
        success: data.saveBoard as boolean,
        errorMessage: data.errorMessage,
      }))
      .then(({ success, errorMessage }:{ success:boolean, errorMessage:string }) => {
        if (success) {
          getBoards(newBoardName);
          showToast('Saved', ToastType.SUCCESS);
        } else if (errorMessage) {
          showToast(errorMessage, ToastType.ERROR);
        } else {
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => {
        setLoading(false)
        setBoardDialogOpen(false);
        setNewBoardName('');
      });
    setSelectedPlayers([]);
  }

  function deleteBoard() {
    setLoading(true);

    gql(`
      mutation {
        deleteBoard(
          boardId: ${Number(selectedBoard)}
        )
      }
    `)
      .then((data:any) => ({
        success: data.deleteBoard as boolean,
        errorMessage: data.errorMessage,
      }))
      .then(({ success, errorMessage }:{ success:boolean, errorMessage:string }) => {
        if (success) {
          getBoards();
          setSelectedBoard('');
          showToast('Saved', ToastType.SUCCESS);
        } else if (errorMessage) {
          showToast(errorMessage, ToastType.ERROR);
        } else {
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => {
        setDeleteBoardDialogOpen(false)
        setLoading(false)
        setNewBoardName('');
        resetFilters(); // reset all filters
      });
    setSelectedPlayers([]);
  }

  // update select board
  function updateBoard(selectedItems: any, filterName = '') {
    if (selectedBoard) {
      setLoading(true);

      // Use selectedItems dynamically and filter 'all' option from the setters
      const filteredItems = filterAllFromSelectedItem(selectedItems);

      // Assign based on filterName
      let positions = filterAllFromSelectedItem(selectedPositions);
      let gradClasses = filterAllFromSelectedItem(selectedGradClasses);
      let teamTypes = filterAllFromSelectedItem(selectedTeamType);
      let states = filterAllFromSelectedItem(selectedState);

      // update latest updated filter
      switch (filterName) {
        case FILTER_TYPE.POSITION:
          positions = filteredItems;
          break;
        case FILTER_TYPE.CLASS:
          gradClasses = filteredItems;
          break;
        case FILTER_TYPE.TEAM_TYPE:
          teamTypes = filteredItems;
          break;
        case FILTER_TYPE.STATE:
          states = filteredItems;
          break;
        default:
          break;
      }

      const boardPayload = {
        id: Number(selectedBoard),
        position: positions,
        class: gradClasses,
        type: teamTypes,
        state: states,
        userId: userId,
      };

      gql(`
        mutation {
          updateBoard(
            Board: ${serializeGQLObject(boardPayload)}
          )
        }
      `)
        .then((data:any) => ({
          success: data.updateBoard as boolean,
          errorMessage: data.errorMessage,
        }))
        .then(({ success, errorMessage }:{ success:boolean, errorMessage:string }) => {
          if (success) {
            getBoards();
            showToast('Saved', ToastType.SUCCESS);
          } else if (errorMessage) {
            showToast(errorMessage, ToastType.ERROR);
          } else {
            showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
          }
        })
        .catch(error => {
          console.error(error);
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        })
        .finally(() => setLoading(false)
        );
      setSelectedPlayers([]);
    }
  };

  function getboardById(id: string) {
    setLoading(true);
    gql(`
      getBoardsById(boardId: ${Number(id)}) 
      {
        id
        name
        userId
        programId
        position
        class
        type
        state
        collegeType
        order
        createdAt
        updatedAt
      }
    `)
    .then((data:any) => data.getBoardsById as any)
    .then((board: Board) => {
      setSelectedBoard(board?.id.toString() || '');
      setSelectedBoardDetails(board);
      setSelectedPositions(positions?.length === board?.position?.length ? ['all', ...(board?.position || [])] : (board?.position || []));
      setSelectedGradClasses(graduatingClasses?.length === board?.class?.length ? ['all', ...(board?.class || [])] : (board?.class || []));
      setSelectedTeamType(teamTypeItems?.length === board?.type?.length ? ['all', ...(board?.type || [])] : (board?.type || []));
      setSelectedState(states?.length === board?.state?.length ? ['all', ...(board?.state || [])] : (board?.state || []));
    })
    .catch(console.error)
    .finally(() => setLoading(false));
  }

  // filter 'all' option from the selected filter list
  function filterAllFromSelectedItem(filterItem: any) {
    return filterItem?.filter((data: string) => data !== 'all') ?? [];
  }

  // reset filters 
  function resetFilters() {
    setSelectedPositions([]);
    setSelectedGradClasses([]);
    setSelectedTeamType([]);
    setSelectedState([]);
  };

  const actionDisabled = !selectedPlayers.length;
  const compareActionDisabled = !selectedPlayers.length || selectedPlayers.length > 1;
  const sideBySideActionDisabled = (
    !selectedPlayers.length ||
    selectedPlayers.length === 1 ||
    selectedPlayers.length > 10
  );
  const boardClearable = selectedPlayers?.length === pinnedPlayers?.length;
  console.log('selectedPositions', selectedPositions.length);
  console.log('selectedGradClasses', selectedGradClasses.length);
  console.log('selectedState', selectedState.length);
  console.log('selectedTeamType', selectedTeamType.length);
  const isDragDisabled = (
    (!selectedPositions?.length &&
    !selectedGradClasses?.length &&
    !selectedState?.length &&
    !selectedTeamType?.length) 
    // ||
    // selectedPositions?.includes('all') ||
    // selectedGradClasses?.includes('all') ||
    // selectedState?.includes('all') ||
    // selectedTeamType?.includes('all')
  );
  const disableNewBoard = !selectedPositions?.length && !selectedGradClasses?.length && !selectedState?.length && !selectedTeamType?.length;
  const boardDropdownItems = boardsList?.length
    ? boardsList.map(board => ({
        content: board.name,
        value: board.id.toString()
      })).sort((a, b) => a.content.localeCompare(b.content))
    : [];
  const isBoardSelected = selectedBoard !== '';

  function renderTableRow (pinnedPlayer:PinnedPlayer, index:number) {
    const {
      player: {
        id,
        firstName,
        lastName,
        photoUrl,
        pai,
        combine,
        multiSport,
        highSchools,
        hsCombines,
        hsSpeedPercentile,
        hsPowerPercentile,
        isSpeedRecruit,
        isPowerRecruit
      },
      position,
    } = pinnedPlayer;

    const primaryHighSchool = (highSchools || []).find((highSchool:HighSchool) => !!highSchool?.isPrimary);

    const itemId = `${id}_${index}`;
    const checked = !!selectedPlayers.find(selectedPlayer => selectedPlayer.id === id);
    const power = Number(hsPowerPercentile);
    const showPower = hsPowerPercentile != null && !isNaN(power);
    const speed = Number(hsSpeedPercentile);
    const showSpeed = hsSpeedPercentile != null && !isNaN(speed);

    return (
      <Draggable
        key={itemId}
        index={index}
        draggableId={itemId}
        isDragDisabled={isDragDisabled}
      >
        {({ draggableProps, dragHandleProps, innerRef }, snapshot) => (
          <TableRow
            key={index}
            className={clsx(classes.tableRow, snapshot.isDragging && classes.tableRowDragging)}
            ref={innerRef}
            {...draggableProps}
            {...dragHandleProps}
          >
            {!isDragDisabled && <TableCell className={clsx(classes.tableCell, printed && classes.blankCell)}>
              <DragNDropDots className={classes.dragNDropDots} />
            </TableCell>}

            <TableCell className={clsx(classes.tableCell, classes.checkBoxCell)}>
              <Checkbox
                checked={checked}
                onChange={onCheckBoxChange(pinnedPlayer?.player)}
              />
            </TableCell>

            <TableCell className={classes.tableCell} align='left'>
              <div className={classes.playerInfoContainer}>
                <Avatar
                  className={classes.playerAvatar}
                  src={photoUrl}
                  alt='Profile picture'
                />

                <div className={classes.playerNameAndHometown}>
                  <PlayerNameWithPopover
                    playerId={id}
                    name={`${firstName} ${lastName}`}
                  />

                  {(primaryHighSchool?.city || primaryHighSchool?.state) && (
                    <div className={classes.playerHometown}>
                      {position} - {primaryHighSchool.city ? `${primaryHighSchool.city}, `: ''}{primaryHighSchool.state ? `${primaryHighSchool.state}` : ''}
                    </div>
                  )}
                </div>

                {primaryHighSchool?.state && (
                  <LocationIcon
                    className={classes.locationIcon}
                    stateCode={primaryHighSchool.state}
                  />
                )}
              </div>
            </TableCell>

            <TableCell className={classes.tableCell}>
              {(primaryHighSchool && primaryHighSchool.graduatingClass)
                ? primaryHighSchool.graduatingClass
                : <NotAvailable />
              }
            </TableCell>

            <TableCell className={classes.tableCell} align='center'>
              <Score
                type={SCORE_TYPE.PAI}
                scoreList={pai}
              />
            </TableCell>

            <TableCell className={clsx(classes.tableCell, classes.hsComTableCell)}>
              <Score
                type={SCORE_TYPE.COMBINE}
                scoreList={combine}
                combines={hsCombines}
              />
            </TableCell>

            <TableCell className={clsx(classes.tableCell, classes.multiSportCell)} align='center'>
              {(multiSport && multiSport.length && multiSport.length > 0) ?
                (
                  <Score
                    className={classes.multiSportsWrap}
                    checkIconClass={classes.checkIconClass}
                    type={SCORE_TYPE.MULTISPORTS}
                    value={multiSport?.length}
                    totalValue={multiSport?.length}
                    multiSportTooltip={ <MultiSportList
                      iconClassName={classes.multiSportIcon}
                      list={multiSport}
                    />
                    }
                  />
                )
                : <MultiSportList
                    iconClassName={classes.multiSportIcon}
                    list={multiSport}
                  />
              }
            </TableCell>

            <TableCell className={clsx(classes.tableCell, classes.speedAndPowerCell, classes.hsSpeedCell)} align='left'>
            <div style={{ fontSize: (speed > 75 && isSpeedRecruit) ? '' : '1rem', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                {showSpeed && (
                  <>
                    <span className={clsx(classes.lowSpeedAndPower)}>{Math.round(speed)}%</span>
                    {speed > 75 && isSpeedRecruit && (
                      <ColorCode
                        className={classes.speedAndPowerRecuitDot}
                        color={Color.ORANGE}
                        tooltipText='Speed Player'
                      />
                    )}
                  </>
                )}
              </div>
            </TableCell>

            <TableCell className={clsx(classes.tableCell, classes.speedAndPowerCell, classes.hsPowerCell)} align='center'>
              <div style={{ fontSize: (power > 75 && isPowerRecruit) ? '' : '1rem', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                {showPower && (
                  <>
                    <span className={clsx(classes.lowSpeedAndPower)}>{Math.round(power)}%</span>
                    {power > 75 && isPowerRecruit && (
                      <ColorCode
                        className={classes.speedAndPowerRecuitDot}
                        color={Color.GREEN}
                        tooltipText='Power Player'
                      />
                    )}
                  </>
                )}
              </div>
            </TableCell>
          </TableRow>
        )}
      </Draggable>
    );
  }

  return (
    <Paper className={clsx(classes.myRecruitingBoard, className)}>
      <Loader inProgress={loading} />

      {!printed && (
        <div className={classes.header}>
          <div className={classes.headerTitleWrap}>
            <PinIcon className={classes.headerIcon} />
            <h2 className={classes.headerTitle}>My Recruiting Board ({pinnedPlayers?.length || 0})</h2>
          </div>
          <div className={classes.boardFilterWrap}>
              <DropDown
                className={classes.dropDown}
                selectorClassName={classes.dropDownSelect}
                selectorRootClassName={classes.selectorRootClassName}
                labelWhenSelectedNone='Select Board'
                labelWhenSelectedAll='Select Board'
                showLabelWhenSelectedNoneWithEmptyItem={true}
                emptyItem={{
                  value: '',
                  content: 'None (Primary)'
                }}
                items={boardDropdownItems}
                value={selectedBoard}
                onChange={(board:string) => {
                  getboardById(board)
                }}
              />
              
              <Button
                className={classes.addBoardButton}
                primary={true}
                type='button'
                disabled={!!selectedBoard || disableNewBoard}
                onClick={() => {
                  setBoardDialogOpen(true);
                }}
              >
                <PlusIcon className={classes.addIcon} />
                <span>New Board</span>
              </Button>

              {/* Delete Board */}
              <Button
                className={classes.addBoardButton}
                primary={true}
                type='button'
                disabled={!selectedBoard}
                onClick={() => {
                  setDeleteBoardDialogOpen(true)
                }}
              >
                {/* <PlusIcon className={classes.addIcon} /> */}
                <span>Delete Board</span>
              </Button>
          </div>
        </div>
      )}

       <Divider variant='fullWidth' light />

      <div className={classes.filterWrap}>
        {/* By Position */}
        <MultiSelect
          className={classes.multiSelect}
          selectorClassName={classes.multiSelectSelector}
          selectorRootClassName={classes.multiSelectSelectorRoot}
          labelWhenSelectedNone='By Position'
          labelWhenSelectedAll='By Position'
          showLabelWhenSelectedNoneWithEmptyItem={true}
          items={positions || []}
          values={selectedPositions}
          emptyItem={{
            content: 'All', value: 'all'
          }}
          onChange={(newValues: string[]) =>
            handlePositionChange(newValues)
          }
          optionsDisbled={isBoardSelected}
        />
        {/* By Class */}
        <MultiSelect
          className={classes.multiSelect}
          selectorClassName={classes.multiSelectSelector}
          selectorRootClassName={classes.multiSelectSelectorRoot}
          labelWhenSelectedNone='By Class'
          labelWhenSelectedAll='By Class'
          showLabelWhenSelectedNoneWithEmptyItem={true}
          items={graduatingClasses || []}
          values={selectedGradClasses}
          emptyItem={{
            content: 'All', value: 'all'
          }}
          onChange={(newValues: string[]) =>
            handleClassChange(newValues)
          }
          optionsDisbled={isBoardSelected}
        />

        <MultiSelect
          className={classes.multiSelect}
          selectorClassName={classes.multiSelectSelector}
          selectorRootClassName={classes.multiSelectSelectorRoot}
          labelWhenSelectedNone='By State'
          labelWhenSelectedAll='By State'
          showLabelWhenSelectedNoneWithEmptyItem={true}
          items={states || []}
          values={selectedState}
          emptyItem={{
            content: 'All', value: 'all'
          }}
          onChange={(newValues: string[]) =>
            handleStateChange(newValues)
          }
          optionsDisbled={isBoardSelected}
        />
        {/* By Type */}
        <MultiSelect
          className={classes.multiSelect}
          selectorClassName={classes.multiSelectSelector}
          selectorRootClassName={classes.multiSelectSelectorRoot}
          labelWhenSelectedNone='By Type'
          labelWhenSelectedAll='By Type'
          showLabelWhenSelectedNoneWithEmptyItem={true}
          items={teamTypeItems}
          values={selectedTeamType}
          emptyItem={{
            content: 'All', value: 'all'
          }}
          onChange={(newValues: string[]) =>
            handleTeamTypeChange(newValues)
          }
          optionsDisbled={isBoardSelected}
        />
        <Action
          className={clsx(classes.action, classes.colorPrimary)}
          onClick={() => {
            setRefreshBoards(true);
          }}
          icon={RefreshIcon}
          iconClassName={classes.actionIcon}
        >
          Refresh
        </Action>
      </div>

      <div className={classes.actions}>
        <div className={classes.actionsPrimary}>
          <Action
            className={clsx(classes.action, sideBySideActionDisabled && classes.disabledAction)}
            icon={SideBySideIcon}
            iconClassName={classes.actionIcon}
            disabled={sideBySideActionDisabled}
            href={`/side-by-side?playerIds=${selectedPlayers.map((player:Player) => player.id).join(',')}`}
          >
            <span className={classes.actionText}>Side-by-Side</span>
          </Action>

          <Action
            className={clsx(classes.action, compareActionDisabled && classes.disabledAction)}
            icon={CompareIcon}
            iconClassName={classes.actionIcon}
            disabled={compareActionDisabled}
            href={!compareActionDisabled ? `/compare/${selectedPlayers[0].slug}` : '/compare'}
          >
            <span className={classes.actionText}>Compare</span>
          </Action>

          <Action
            className={clsx(classes.action, actionDisabled && classes.disabledAction)}
            icon={AlertIcon}
            iconClassName={classes.actionIcon}
            disabled={actionDisabled}
            onClick={onAlert}
          >
            <span className={classes.actionText}>Alert</span>
          </Action>

          <Action
            className={clsx(classes.action, actionDisabled && classes.disabledAction)}
            icon={SaveIcon}
            iconClassName={classes.actionIcon}
            disabled={actionDisabled}
            onClick={onSave}
          >
            <span className={classes.actionText}>Save</span>
          </Action>

          <Action
            className={clsx(classes.action, actionDisabled && classes.disabledAction)}
            icon={PinIcon}
            iconClassName={classes.actionIcon}
            disabled={actionDisabled}
            onClick={handleOpenClearDialog}
          >
            <span className={classes.actionText}>Unpin</span>
          </Action>
        </div>
        <div className={classes.headerDownloads}>
          {(user.accessLevel.exportAllowanceType !== 'none') && (
            <Download
              label='XLS'
              onClick={onXLSDownload}
              disabled={!players.length}
            />
          )}

          <Download
            label='PDF'
            onClick={onPDFDownload}
          />
        </div>
      </div>

      <Dialog
        open={boardDialogOpen}
        title='Save New Board'
        onClose={() => setBoardDialogOpen(false)}
        actions={[
          {
            name: 'Cancel',
            onClick: () => {
              setBoardDialogOpen(false);
              setNewBoardName('');
            },
          },
          {
            name: 'Confirm',
            primary: true,
            disabled: !newBoardName.trim(),
            loading,
            onClick: () => {
              addBoardHandler();
            },
          }
        ]}
      >
        <div className={classes.boardDialogContent}>
          <Input
            className={classes.boardDialogInput}
            label='Board Name'
            value={newBoardName}
            onChange={(value:string) => setNewBoardName(value)}
          />
        </div>
      </Dialog>

      <Divider variant='fullWidth' light />

      {!players.length && (
        <div className={classes.noPlayers}>
          <PinIcon className={classes.noPlayersIcon} />
          <div className={classes.noPlayersText}>No Player Pinned</div>
        </div>
      )}

      {players.length > 0 && (
        <div className={classes.tableWrap}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId='recruiting-board'>
              {({ droppableProps, placeholder, innerRef }) => (
                <div
                  ref={innerRef}
                  {...droppableProps}
                >
                  <Table className={classes.table} stickyHeader aria-label="stick table">
                    <TableHead>
                      <TableRow>
                        {!isDragDisabled ? <TableCell className={clsx(classes.tableHeadCell, printed && classes.blankCell)}>&nbsp;</TableCell> : ''}
                        <TableCell
                          key='check-box-cell'
                          className={classes.checkBoxCell}
                        >
                          <Checkbox
                            checked={selectedPlayers.length === pinnedPlayers.length}
                            onChange={() => selectedPlayers.length === pinnedPlayers.length
                              ? setSelectedPlayers([])
                              : setSelectedPlayers(pinnedPlayers.map((pinnedPlayer) => pinnedPlayer.player))
                            }
                          />
                        </TableCell>
                        <SortableTableHeaderCell
                          label={<>Name&nbsp;|&nbsp;Hometown</>}
                          order={sortConfig.metric === 'name' ? sortConfig.order : undefined}
                          onClick={() => handleSortClick('name')}
                          tableHeadCellClassName={classes.tableHeadCell}
                          orderIconClassName={classes.orderIcon}
                          orderIconActiveClassName={classes.orderIconActive}
                        />

                        <SortableTableHeaderCell
                          label="Class"
                          order={sortConfig.metric === 'class' ? sortConfig.order : undefined}
                          onClick={() => handleSortClick('class')}
                          tableHeadCellClassName={classes.tableHeadCell}
                          orderIconClassName={classes.orderIcon}
                          orderIconActiveClassName={classes.orderIconActive}
                        />

                        <SortableTableHeaderCell
                          label="PAI"
                          order={sortConfig.metric === 'pai' ? sortConfig.order : undefined}
                          onClick={() => handleSortClick('pai')}
                          tableHeadCellClassName={classes.tableHeadCell}
                          orderIconClassName={classes.orderIcon}
                          orderIconActiveClassName={classes.orderIconActive}
                        />

                        <SortableTableHeaderCell
                          label="HS Com."
                          order={sortConfig.metric === 'combine' ? sortConfig.order : undefined}
                          onClick={() => handleSortClick('combine')}
                          tableHeadCellClassName={classes.tableHeadCell}
                          orderIconClassName={classes.orderIcon}
                          orderIconActiveClassName={classes.orderIconActive}
                        />
                        <SortableTableHeaderCell
                          label="HS Sports"
                          order={sortConfig.metric === 'hsSports' ? sortConfig.order : undefined}
                          onClick={() => handleSortClick('hsSports')}
                          tableHeadCellClassName={clsx(classes.tableHeadCell, classes.multiSportCell)}
                          orderIconClassName={classes.orderIcon}
                          orderIconActiveClassName={classes.orderIconActive}
                        />
                        <SortableTableHeaderCell
                          label="HS Speed"
                          order={sortConfig.metric === 'speed' ? sortConfig.order : undefined}
                          onClick={() => handleSortClick('speed')}
                          tableHeadCellClassName={classes.tableHeadCell}
                          orderIconClassName={classes.orderIcon}
                          orderIconActiveClassName={classes.orderIconActive}
                        />
                        <SortableTableHeaderCell
                          label="HS Power"
                          order={sortConfig.metric === 'power' ? sortConfig.order : undefined}
                          onClick={() => handleSortClick('power')}
                          tableHeadCellClassName={classes.tableHeadCell}
                          orderIconClassName={classes.orderIcon}
                          orderIconActiveClassName={classes.orderIconActive}
                        />
                        {!isDragDisabled ? <TableCell className={clsx(classes.tableHeadCell, printed && classes.blankCell)}>&nbsp;</TableCell> : ''}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pinnedPlayers.map(renderTableRow)}
                      {placeholder}
                    </TableBody>
                  </Table>
                  {!pinnedPlayers?.length && (
                    <div className={classes.noDataTextWrapper}>
                      <Typography className={classes.noDataText}>No Data Found</Typography>
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}

      <Toast
        visible={toastVisible}
        type={toastType}
        onHide={() => setToastVisible(false)}
      >
        {toastMessage}
      </Toast>

      <ConfirmDialog
        title={`Remove ${boardClearable ? 'all players' : 'player'} from primary and saved boards for all users of your program?`}
        confirmActionText={boardClearable ? 'Yes, Clear the board' : 'Yes, Confirm'}
        open={dialogOpen}
        onConfirm={boardClearable ? clearBoard : unpinSinglePlayer}
        onClose={() => setDialogOpen(false)}
        onCancel={() => setDialogOpen(false)}
      />

      {/* Delete board dialog */}
      <ConfirmDialog
        title='Are you sure you want to delete?'
        open={deleteBoardDialogOpen}
        onConfirm={deleteBoard}
        onClose={() => setDeleteBoardDialogOpen(false)}
        onCancel={() => setDeleteBoardDialogOpen(false)}
      />
    </Paper>
  );
}
